import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { HardBreakNodeSchema, TextNodeSchema } from '../identity/document-templates.schemas';

/** The immoOS-Standard's fixed id: it is not stored, so every workspace addresses it by this one value. */
export const BUYER_CONTRACT_STANDARD_TEMPLATE_ID = '00000000-0000-4000-8000-000000001431';

/** The Käufer data fields: exactly the tokens the Muster fills today (`MUSTER_TOKENS`), in palette order. */
export const BUYER_CONTRACT_FIELD_KEYS = [
  'companyName',
  'companyAddress',
  'companyEmail',
  'companyPhone',
  'buyerName',
  'commission',
  'commissionNote',
  'propertyTitle',
  'propertyCity',
  'noticeSentOn',
  'concludedOn',
  'withdrawalPeriodEndsOn',
] as const;
export const BuyerContractFieldKeySchema = z.enum(BUYER_CONTRACT_FIELD_KEYS);
export type BuyerContractFieldKey = z.infer<typeof BuyerContractFieldKeySchema>;

export const BUYER_CONTRACT_FIELD_GROUPS = ['COMPANY', 'BUYER', 'PROPERTY', 'NOTICE', 'CONTRACT'] as const;
export type BuyerContractFieldGroup = (typeof BUYER_CONTRACT_FIELD_GROUPS)[number];

const BUYER_CONTRACT_FIELD_GROUP_BY_KEY: { readonly [K in BuyerContractFieldKey]: BuyerContractFieldGroup } = {
  companyName: 'COMPANY',
  companyAddress: 'COMPANY',
  companyEmail: 'COMPANY',
  companyPhone: 'COMPANY',
  buyerName: 'BUYER',
  commission: 'BUYER',
  commissionNote: 'BUYER',
  propertyTitle: 'PROPERTY',
  propertyCity: 'PROPERTY',
  noticeSentOn: 'NOTICE',
  concludedOn: 'CONTRACT',
  withdrawalPeriodEndsOn: 'CONTRACT',
};

export function buyerContractFieldGroup(key: BuyerContractFieldKey): BuyerContractFieldGroup {
  return BUYER_CONTRACT_FIELD_GROUP_BY_KEY[key];
}

// A Käufer chip stores its key only: no `required`, the Muster's optional-token rule decides emptiness.
export const BuyerContractDataFieldNodeSchema = z.object({
  type: z.literal('dataField'),
  attrs: z.object({ key: BuyerContractFieldKeySchema }).strict(),
});

const InlineNodeSchema = z.discriminatedUnion('type', [
  TextNodeSchema,
  HardBreakNodeSchema,
  BuyerContractDataFieldNodeSchema,
]);
const ParagraphNodeSchema = z.object({ type: z.literal('paragraph'), content: z.array(InlineNodeSchema).optional() });
const HeadingNodeSchema = z.object({
  type: z.literal('heading'),
  attrs: z.object({ level: z.union([z.literal(1), z.literal(2), z.literal(3)]) }),
  content: z.array(InlineNodeSchema).optional(),
});

export type BuyerContractParagraphNode = z.infer<typeof ParagraphNodeSchema>;
export type BuyerContractBulletListNode = { type: 'bulletList'; content: BuyerContractListItemNode[] };
export type BuyerContractOrderedListNode = {
  type: 'orderedList';
  attrs?: { start?: number | undefined } | undefined;
  content: BuyerContractListItemNode[];
};
export type BuyerContractListItemNode = {
  type: 'listItem';
  content: Array<BuyerContractParagraphNode | BuyerContractBulletListNode | BuyerContractOrderedListNode>;
};

const ListItemNodeSchema: z.ZodType<BuyerContractListItemNode> = z.object({
  type: z.literal('listItem'),
  content: z
    .array(z.union([ParagraphNodeSchema, z.lazy(() => BulletListNodeSchema), z.lazy(() => OrderedListNodeSchema)]))
    .min(1),
});
const BulletListNodeSchema: z.ZodType<BuyerContractBulletListNode> = z.object({
  type: z.literal('bulletList'),
  content: z.array(ListItemNodeSchema).min(1),
});
const OrderedListNodeSchema: z.ZodType<BuyerContractOrderedListNode> = z.object({
  type: z.literal('orderedList'),
  attrs: z.object({ start: z.number().int().min(1).optional() }).optional(),
  content: z.array(ListItemNodeSchema).min(1),
});

// One block: headings (1–3), paragraphs, bullet and ordered lists; no table, no page break.
export const BuyerContractBlockSchema = z.object({
  type: z.literal('doc'),
  content: z
    .array(z.union([ParagraphNodeSchema, HeadingNodeSchema, BulletListNodeSchema, OrderedListNodeSchema]))
    .min(1),
});
export type BuyerContractBlock = z.infer<typeof BuyerContractBlockSchema>;

const FormattedInlineNodeSchema = z.discriminatedUnion('type', [TextNodeSchema, HardBreakNodeSchema]);
const FormattedParagraphNodeSchema = z.object({
  type: z.literal('paragraph'),
  content: z.array(FormattedInlineNodeSchema).optional(),
});
const FormattedHeadingNodeSchema = z.object({
  type: z.literal('heading'),
  attrs: z.object({ level: z.union([z.literal(1), z.literal(2), z.literal(3)]) }),
  content: z.array(FormattedInlineNodeSchema).optional(),
});

export type FormattedInlineNode = z.infer<typeof FormattedInlineNodeSchema>;
export type FormattedParagraphNode = z.infer<typeof FormattedParagraphNodeSchema>;
export type FormattedHeadingNode = z.infer<typeof FormattedHeadingNodeSchema>;
export type FormattedBulletListNode = { type: 'bulletList'; content: FormattedListItemNode[] };
export type FormattedOrderedListNode = {
  type: 'orderedList';
  attrs?: { start?: number | undefined } | undefined;
  content: FormattedListItemNode[];
};
export type FormattedListItemNode = {
  type: 'listItem';
  content: Array<FormattedParagraphNode | FormattedBulletListNode | FormattedOrderedListNode>;
};

const FormattedListItemNodeSchema: z.ZodType<FormattedListItemNode> = z.object({
  type: z.literal('listItem'),
  content: z
    .array(
      z.union([
        FormattedParagraphNodeSchema,
        z.lazy(() => FormattedBulletListNodeSchema),
        z.lazy(() => FormattedOrderedListNodeSchema),
      ]),
    )
    .min(1),
});
const FormattedBulletListNodeSchema: z.ZodType<FormattedBulletListNode> = z.object({
  type: z.literal('bulletList'),
  content: z.array(FormattedListItemNodeSchema).min(1),
});
const FormattedOrderedListNodeSchema: z.ZodType<FormattedOrderedListNode> = z.object({
  type: z.literal('orderedList'),
  attrs: z.object({ start: z.number().int().min(1).optional() }).optional(),
  content: z.array(FormattedListItemNodeSchema).min(1),
});

// A block with its chips filled: no `dataField`. The top-level content carries no min(1), a read schema stays no stricter than its writer (BL-039).
export const FormattedLegalTextSchema = z.object({
  type: z.literal('doc'),
  content: z.array(
    z.union([
      FormattedParagraphNodeSchema,
      FormattedHeadingNodeSchema,
      FormattedBulletListNodeSchema,
      FormattedOrderedListNodeSchema,
    ]),
  ),
});
export type FormattedLegalText = z.infer<typeof FormattedLegalTextSchema>;

export const BUYER_CONTRACT_BLOCK_NAMES = ['contract', 'withdrawalNotice', 'withdrawalForm'] as const;
export type BuyerContractBlockName = (typeof BUYER_CONTRACT_BLOCK_NAMES)[number];
export const BuyerContractBlocksSchema = z
  .object({
    contract: BuyerContractBlockSchema,
    withdrawalNotice: BuyerContractBlockSchema,
    withdrawalForm: BuyerContractBlockSchema,
  })
  .strict();
export type BuyerContractBlocks = z.infer<typeof BuyerContractBlocksSchema>;

export const BUYER_CONTRACT_TEMPLATE_KINDS = ['STANDARD', 'OWN'] as const;
export const BuyerContractTemplateKindSchema = z.enum(BUYER_CONTRACT_TEMPLATE_KINDS);
export type BuyerContractTemplateKind = z.infer<typeof BuyerContractTemplateKindSchema>;

export const BuyerContractTemplateSummarySchema = z.object({
  id: z.uuid(),
  kind: BuyerContractTemplateKindSchema,
  // null for STANDARD: its fixed name is copy, shown by the apps from i18n.
  name: z.string().nullable(),
  active: z.boolean(),
  // STANDARD: the Muster version (`'1'` today); OWN: '1', '2', … per workspace.
  version: z.string(),
  // STANDARD: the release date of its current version; OWN: the current version's save.
  updatedAt: z.iso.datetime(),
  createdByMemberId: refersTo('member', z.uuid()).nullable(),
  createdByName: z.string().nullable(),
  // Who saved the current version; null for STANDARD.
  savedByMemberId: refersTo('member', z.uuid()).nullable(),
  savedByName: z.string().nullable(),
});
export type BuyerContractTemplateSummary = z.infer<typeof BuyerContractTemplateSummarySchema>;

export const ListBuyerContractTemplatesResponseSchema = z.object({ data: z.array(BuyerContractTemplateSummarySchema) });
export type ListBuyerContractTemplatesResponse = z.infer<typeof ListBuyerContractTemplatesResponseSchema>;

export const BuyerContractTemplateSchema = BuyerContractTemplateSummarySchema.extend({
  blocks: BuyerContractBlocksSchema,
});
export type BuyerContractTemplate = z.infer<typeof BuyerContractTemplateSchema>;

export const BuyerContractTemplatesPathParamsSchema = z.object({ workspaceId: z.uuid() });
export const BuyerContractTemplatePathParamsSchema = z.object({
  workspaceId: z.uuid(),
  buyerContractTemplateId: z.uuid(),
});

export const BUYER_CONTRACT_TEMPLATE_NAME_MAX_LENGTH = 80;

export const CreateBuyerContractTemplateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(BUYER_CONTRACT_TEMPLATE_NAME_MAX_LENGTH),
    blocks: BuyerContractBlocksSchema,
  })
  .strict();
export type CreateBuyerContractTemplateBody = z.infer<typeof CreateBuyerContractTemplateBodySchema>;

// No name: as for every Vorlage, the name is fixed at creation (IMM-270 AC 18).
export const UpdateBuyerContractTemplateBodySchema = z.object({ blocks: BuyerContractBlocksSchema }).strict();
export type UpdateBuyerContractTemplateBody = z.infer<typeof UpdateBuyerContractTemplateBodySchema>;

type BuyerContractNode = {
  readonly type: string;
  readonly text?: string | undefined;
  readonly content?: ReadonlyArray<BuyerContractNode> | undefined;
};

function hasContent(node: BuyerContractNode): boolean {
  if (node.type === 'dataField') return true;

  if (node.type === 'text') return /\S/.test(node.text ?? '');

  return (node.content ?? []).some(hasContent);
}

function isEmptyBuyerContractBlock(block: BuyerContractBlock): boolean {
  return !hasContent(block);
}

/** The blocks with no non-whitespace text and no data field anywhere in them, in BUYER_CONTRACT_BLOCK_NAMES order. */
export function emptyBuyerContractBlockNames(blocks: BuyerContractBlocks): BuyerContractBlockName[] {
  return BUYER_CONTRACT_BLOCK_NAMES.filter((name) => isEmptyBuyerContractBlock(blocks[name]));
}
